/* eslint-disable no-unsafe-optional-chaining */
import styled from 'styled-components';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { push } from 'redux-first-history';
import { compose } from 'react-recompose';
import { Link } from '@material-ui/core';
import {
    Alert,
    Container,
    ContentLoader,
    formatNumber,
    Header,
    palette,
    TabsPanel,
    Typography,
    UploadProgress,
} from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import { AccountWarnings, CampaignType } from '@dev/ee-api/ee-api-models';
import { find, floor, isEmpty, isNil, truncate } from 'lodash-es';

import ContactEventHistoryOverlay from '@/components/Activity/ContactEventHistory/Overlay';
import { isReseller } from '@/services/Secuirty';
import { EmailsState } from '@/store/emails/types';
import { ApplicationState, BrowserInterface } from '@/store';
import { CampaignsInterface } from '@/store/campaigns/types';
import { GetAccountWarnings } from '@/store/account/actions';
import {
    CancelPollingInProgressCampaignByChannelName,
    GetInProgressCampaignByChannelName,
    PollInProgressCampaignByChannelName,
    SetPickedContactEmail,
} from '@/store/campaigns/actions';

import SingleContactOverlay from '../../../SingleContactOverlay';
import Activity from './Activity';
import Overview from './Overview';
import InProgressInterface, { InProgressOverviewState } from './InProgressInterface';
import { AccountState } from '../../../../store/account/types';
import { CampaignsPaths } from '../../../../services/dictionaries/Paths';
import { links } from '../../../../services/dictionaries/links';

interface StateToProps {
    browser?: BrowserInterface;
    account?: AccountState;
    campaigns?: CampaignsInterface;
    emails?: EmailsState;
}

type Props = StateToProps & DispatchProps & InProgressInterface;

const mapStateToProps = (state: ApplicationState, ownProps: Props): StateToProps => ({
    ...ownProps,
    browser: state.browser,
    emails: state.emails,
    account: state.account,
    campaigns: state.campaigns,
});

const dispatchProps = {
    Push: push,
    GetAccountWarnings,
    GetInProgressCampaignByChannelName,
    CancelPollingInProgressCampaignByChannelName,
    PollInProgressCampaignByChannelName,
    SetPickedContactEmail,
};

type DispatchProps = {
    Push(url: string): void;
    GetAccountWarnings(): void;
    GetInProgressCampaignByChannelName: (param: string) => void;
    CancelPollingInProgressCampaignByChannelName: () => void;
    PollInProgressCampaignByChannelName: (param: string) => void;
    SetPickedContactEmail: (email: string) => void;
};

const TopPanels = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding-bottom: 48px;
`;

type StyledProps = {
    isMobile?: boolean;
};

const Panel = styled.div<StyledProps>`
    padding: 24px 32px;
    flex: ${({ isMobile }) => (isMobile ? '' : '1 1 20%')};
    margin-right: ${({ isMobile }) => (isMobile ? '0' : '30px')};
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    width: ${({ isMobile }) => (isMobile ? '100%' : '')};

    &:last-of-type {
        margin-right: 0px;
        margin-bottom: 0px;
        margin-top: ${({ isMobile }) => (isMobile ? '32px' : '0')};
    }
`;

const PanelTopInlineValue = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    .MuiTypography-h1 {
        margin-right: 12px;
    }
`;

const AlertWrapper = styled.div`
    padding-top: 32px;
    .MuiAlert-icon {
        align-items: flex-start;
    }
`;

const BannerContainer = styled.div`
    margin: 64px 0;
`;
const ContentLoaderWrapper = styled.div`
    min-width: 20px;
    flex: 1;
`;

class CampaignReportsInProgress extends Component<Props> {
    public state: InProgressOverviewState = {
        tabValue: 0,
    };

    public componentDidMount(): void {
        const { GetInProgressCampaignByChannelName, PollInProgressCampaignByChannelName, GetAccountWarnings, match } =
            this.props;

        GetAccountWarnings();

        if (match.path === CampaignsPaths.CampaignReportInProgressActivity) {
            this.setState({ tabValue: 1 });
        }

        const { name } = match.params;
        const decodedName = safelyDecodeBase64Url(name);

        GetInProgressCampaignByChannelName(decodedName);
        PollInProgressCampaignByChannelName(decodedName);
    }

    private tabsOnClick(ev, newValue): void {
        const { Push, match } = this.props;
        this.setState({ tabValue: newValue }, () => {
            if (!newValue) {
                Push(
                    CampaignsPaths.CampaignReportInProgress.replace(':type', match.params.type).replace(
                        ':name',
                        match.params.name
                    )
                );
                return;
            }
            Push(
                CampaignsPaths.CampaignReportInProgressActivity.replace(':type', match.params.type).replace(
                    ':name',
                    match.params.name
                )
            );
        });
    }

    private renderSendingCampaignBanner(): JSX.Element {
        const {
            t,
            campaigns: { inProgressCampaign },
        } = this.props;

        return (
            <BannerContainer>
                <UploadProgress
                    percentage={
                        !inProgressCampaign?.CompletedPercentage ? 0 : floor(inProgressCampaign?.CompletedPercentage)
                    }
                    textPrimary={t('wearesendingyourcampaign')}
                    textSecondary={t('seethenumberofemailswaiting')}
                />
            </BannerContainer>
        );
    }

    private renderTopPanels(): JSX.Element {
        const {
            t,
            campaigns: { inProgressCampaign },
            browser,
        } = this.props;
        const isMobile = browser.lessThan.hd;

        return (
            <TopPanels>
                <Panel isMobile={isMobile}>
                    <PanelTopInlineValue>
                        <Typography variant={'h1'} display="inline" style={{ fontWeight: '900' }}>
                            {!isNil(inProgressCampaign?.Count) ? (
                                formatNumber(inProgressCampaign?.Count)
                            ) : (
                                <ContentLoaderWrapper>
                                    <ContentLoader mode={'row'} height={28} />
                                </ContentLoaderWrapper>
                            )}
                        </Typography>
                        <Typography variant={'h3'} display="inline">
                            {t('sending')}
                        </Typography>
                    </PanelTopInlineValue>
                    <Typography variant={'body1'} bodyVariant={'help'}>
                        {t('inprogressemailssending')}
                    </Typography>
                </Panel>
                <Panel isMobile={isMobile}>
                    <PanelTopInlineValue>
                        <Typography variant={'h1'} style={{ fontWeight: '900' }}>
                            {!isNaN(inProgressCampaign?.Count - inProgressCampaign?.Ready) ? (
                                formatNumber((inProgressCampaign?.Count || 0) - (inProgressCampaign?.Ready || 0))
                            ) : (
                                <ContentLoaderWrapper>
                                    <ContentLoader mode={'row'} height={28} />
                                </ContentLoaderWrapper>
                            )}
                        </Typography>
                        <Typography variant={'h3'} display="inline">
                            {t('waiting')}
                        </Typography>
                    </PanelTopInlineValue>
                    <Typography variant={'body1'} bodyVariant={'help'}>
                        {`${t('inprogressemailswaiting')} `}
                        {!isReseller() && (
                            <Link target="_blank" rel="noreferrer" href={links.whatisemaildeliverystatus}>
                                {t('learnmore')}
                            </Link>
                        )}
                    </Typography>
                </Panel>
            </TopPanels>
        );
    }

    private renderAlert(body: JSX.Element, severity: 'warning' | 'error' | 'info', isInfo: boolean): JSX.Element {
        return <Alert severity={severity} body={body} isInfo={isInfo} />;
    }

    public componentWillUnmount(): void {
        const { CancelPollingInProgressCampaignByChannelName } = this.props;

        CancelPollingInProgressCampaignByChannelName();
    }

    private getAccountWarnings(): JSX.Element {
        const {
            t,
            account: { warnings },
        } = this.props;

        if (isEmpty(warnings)) {
            return null;
        }

        if (find(warnings, AccountWarnings.DeliveryPausedAccountMaintenance)) {
            return (
                <AlertWrapper>
                    {this.renderAlert(<div>{t('emaildeliveryistemporarilypaused')}</div>, 'warning', false)}
                </AlertWrapper>
            );
        }
    }

    public render(): JSX.Element {
        const { match, t, campaigns, emails, browser } = this.props;
        const isMobile = browser.lessThan.hd;

        const contactEmail = campaigns?.pickedContactEmail;
        const resetContactEmail = (): void => {
            this.props.SetPickedContactEmail(null);
        };

        return (
            <>
                <Container>
                    <Header
                        header={{ title: t('inprogress') }}
                        breadcrumbs={{
                            text: truncate(safelyDecodeBase64Url(match.params.name), {
                                length: isMobile ? 40 : 75,
                            }),
                            onClick: (): void =>
                                this.props.Push(
                                    (match.params.type === CampaignType.AbSplit
                                        ? CampaignsPaths.CampaignAbResults
                                        : CampaignsPaths.CampaignOverview
                                    )
                                        .replace(':type', match.params.type)
                                        .replace(':name', match.params.name)
                                ),
                        }}
                    />
                    {this.getAccountWarnings()}
                    {this.renderSendingCampaignBanner()}
                    {this.renderTopPanels()}
                    <TabsPanel
                        tabs={[{ label: t('overview') }, { label: t('details') }]}
                        onChange={this.tabsOnClick.bind(this)}
                        value={this.state.tabValue}
                    />
                    <Switch>
                        <Route exact component={Overview} path={CampaignsPaths.CampaignReportInProgress} />
                        <Route exact component={Activity} path={CampaignsPaths.CampaignReportInProgressActivity} />
                    </Switch>
                </Container>
                {campaigns.pickedContactEmail && (
                    <SingleContactOverlay email={contactEmail} action={resetContactEmail} />
                )}
                {emails.msgId && <ContactEventHistoryOverlay />}
            </>
        );
    }
}

export default compose(
    withTranslation(),
    connect<StateToProps, DispatchProps, Props>(mapStateToProps, dispatchProps)
)(CampaignReportsInProgress);
